import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import { store } from '~/store';
import { selectCurrentOrganizationId } from '~/organizations/selectors';
import { fileFromUrl } from '~/uploads/lib/remoteFile';
import { OnFileReadyCb } from '~/uploads/types';
import {
  CanvaDesignConfig,
  CanvaDesignConfigValues,
  CanvaLiterals,
} from './constants';

declare global {
  interface Window {
    canvaApi?: {
      createDesign: (args: Record<string, unknown>) => void;
      editDesign: (args: Record<string, unknown>) => void;
    };
  }
}

export const getCanvaDesignConfig = (serviceId: string): CanvaDesignConfig => {
  const config = CanvaDesignConfigValues.find((s) => s.service === serviceId);

  if (!config) throw new Error('Missing design option for canva');

  return config;
};

const getCanvaChannel = (serviceId): string => {
  return serviceId === 'omni'
    ? 'multiple'
    : getCanvaDesignConfig(serviceId).service;
};

const getTrackingMetadata = (serviceId, designId?: string) => {
  const organizationId = selectCurrentOrganizationId(store.getState());
  return {
    organizationId,
    clientName: 'publishWeb',
    product: 'publish',
    channel: getCanvaChannel(serviceId),
    designId,
  };
};

type CanvaPublishedOpts = {
  designId: string;
  exportUrl: string;
};

const uploadCanvaDesign = async (
  opts: CanvaPublishedOpts,
  onFileReady: OnFileReadyCb
) => {
  onFileReady(
    await fileFromUrl({
      remoteUrl: opts.exportUrl,
      name: CanvaLiterals.CanvaFilename,
    }),
    {
      name: 'canva',
      id: opts.designId,
    }
  );
};

const editTracking = {
  clicked: BufferTracker.canvaButtonEditClicked,
  published: BufferTracker.canvaDesignRepublished,
  attached: BufferTracker.canvaImageEditAttached,
  designOpen: BufferTracker.canvaDesignEditOpened,
  designClose: BufferTracker.canvaDesignEditClosed,
};

const createTracking = {
  clicked: BufferTracker.canvaButtonClicked,
  published: BufferTracker.canvaDesignPublished,
  attached: BufferTracker.canvaImageAttached,
  designOpen: BufferTracker.canvaDesignOpened,
  designClose: BufferTracker.canvaDesignClosed,
};

export const openCanvaModal = (options: {
  onFileReady: OnFileReadyCb;
  designConfig: CanvaDesignConfig;
  designId?: string;
  onClose?: () => void;
}) => {
  const { onFileReady, designConfig, designId, onClose } = options;

  const metadata = getTrackingMetadata(designConfig.service, designId);

  let canvaFunc;
  let design;
  let track;
  if (designId) {
    // when we have a designId it's an editing action
    canvaFunc = window?.canvaApi?.editDesign;
    design = { id: designId };
    track = editTracking;
  } else {
    // otherwise we create a new design with specific dimensions
    canvaFunc = window?.canvaApi?.createDesign;
    design = { title: designConfig.title, dimensions: designConfig.dimensions };
    track = createTracking;
  }

  track.clicked(metadata);

  canvaFunc?.({
    design,
    editor: {
      publishLabel: CanvaLiterals.PublishLabel,
      fileType: CanvaLiterals.FileType,
    },
    onDesignPublish: (opts: CanvaPublishedOpts) => {
      track.published({ ...metadata, designId: opts.designId });
      onClose?.();
      uploadCanvaDesign(opts, onFileReady).then(() => {
        track.attached({ ...metadata, designId: opts.designId });
      });
    },
    onDesignOpen: () => {
      track.designOpen(metadata);
    },
    onDesignClose: () => {
      onClose?.();
      track.designClose(metadata);
    },
  });
};
